import { Link as RouterLink } from "react-router";

import { Link as MuiLink, TypographyVariant } from "@mui/material";

import { ClientMasterDataTab, clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

interface Props {
  clientId: string;
  name: string;
  variant?: TypographyVariant;
  /**
   * Master-data tab to open instead of the client file's default landing tab. The link always
   * targets `clientId`; this only picks which of its tabs opens.
   */
  masterDataTab?: ClientMasterDataTab;
  /** Strikes the name through (client marked for removal from an appointment). */
  strikeThrough?: boolean;
  /**
   * Opens the client file in a new tab instead of navigating in place. For links inside a form
   * dialog, where navigating away would trip the unsaved-changes blocker and discard the edits.
   */
  openInNewTab?: boolean;
}

export function ClientLink({ clientId, name, variant, masterDataTab, strikeThrough, openInNewTab }: Props) {
  const { t } = useTranslation();

  return (
    <MuiLink
      component={RouterLink}
      to={
        masterDataTab
          ? clientPath.byMasterDataTab({ id: clientId, tab: masterDataTab })
          : clientPath.byId({ id: clientId })
      }
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
      onClick={(e) => e.stopPropagation()}
      aria-label={t(openInNewTab ? "clients.openClientInNewTab" : "clients.openClient", { name })}
      underline="hover"
      variant={variant}
      sx={{ fontWeight: 700, color: "text.primary", textDecoration: strikeThrough ? "line-through" : undefined }}
    >
      {name}
    </MuiLink>
  );
}
